import { useCallback } from "react"
import { download, generateCsv, mkConfig } from "export-to-csv"
import { BalanceSelectOrderType, ParsedPaymentDetails } from "../supplierAcquiringReportsTypes"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../localization/useDateTime"
import { getCurrency } from "../../../../currency/currencyLib"
import { useMoney } from "../../../../currency/useMoney"
import { classifyPaymentRevenue } from "../reconciliationLib"
import { Interval } from "luxon"

export function useExportAcquiringReportsBalanceDetailsToCsv(
    data: ParsedPaymentDetails[],
    reportPeriod: Interval,
    orderPeriod: BalanceSelectOrderType,
) {
    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()
    const moneyFactory = useMoney({ locale: "da-DK" })

    // Interval end is exclusive but we want an inclusive date range
    const reportEndDate = reportPeriod.end.minus({ days: 1 })
    const formattedStartDate = dateTimeFactory.formatDate(reportPeriod.start)
    const formattedEndDate = dateTimeFactory.formatDate(reportEndDate)

    const csvOptions = mkConfig({
        fieldSeparator: ";",
        quoteStrings: true,
        decimalSeparator: ".",
        useBom: true,
        useKeysAsHeaders: true,
        filename: `Facilitynet-reconciliation-balance-details-${l10n.getStringForEnum(
            "supplier-overview-balance-details-dialog-select-order-mode",
            orderPeriod ?? "",
            "",
        )}-${formattedStartDate}_${formattedEndDate}`,
    })

    const mapOrderToCsvRow = useCallback((order: ParsedPaymentDetails) => {
        const currency = order.effectiveAmount ? getCurrency(order.effectiveAmount) : undefined
        const revenueType = order.bookingDate ? classifyPaymentRevenue(order, reportPeriod) : undefined

        return {
            "Order ID": order?.orderId ?? "",
            "PSP Reference": order?.pspPaymentReference ?? "",
            "Booked date": order?.bookingDate
                ? dateTimeFactory.formatDateTime(order.bookingDate)
                : "",
            "Available date": order?.valueDate
                ? dateTimeFactory.formatDate(order.valueDate)
                : "",
            "Currency": currency
                ? moneyFactory.getLocalizedCurrencySymbol(currency)
                : "",
            "Gross amount": order?.grossAmount ? moneyFactory.formatAsNumber(order.grossAmount) : "",
            "Fee amount": order.feeAmount ? moneyFactory.formatAsNumber(order.feeAmount) : "",
            "Effective amount": order?.effectiveAmount ? moneyFactory.formatAsNumber(order.effectiveAmount) : "",
            "Revenue type": revenueType ? l10n.getStringForEnum("supplier-overview-balance-details-dialog-table-payment-type", revenueType, revenueType) : "",
        }
    }, [reportPeriod, dateTimeFactory, moneyFactory, l10n])

    return useCallback(() => {
        let exportRows: ReturnType<typeof mapOrderToCsvRow>[]

        if (data && data.length > 0) {
            exportRows = data.map((order) => mapOrderToCsvRow(order))
        } else {
            exportRows = [mapOrderToCsvRow({} as ParsedPaymentDetails)]
        }

        const csv = generateCsv(csvOptions)(exportRows)
        download(csvOptions)(csv)
    }, [mapOrderToCsvRow, data])
}
